import { createUrlObject, fixOdataDateOffsetFilter, getUrlObjectHref } from '@asamco/utils';
import ODataStore from 'devextreme/data/odata/store';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';

// import DevExpress from 'devextreme/bundles/dx.all';
declare namespace DevExpress.ui {
	// tslint:disable:class-name
	export interface dxDataGridOptions {
		[x: string]: any;
	}
	export interface dxDataGrid {
		[x: string]: any;
	}
	export interface dxToolbarOptions {
		[x: string]: any;
	}
	export interface dxDataGridEditing {
		[x: string]: any;
	}
	export interface GridBase {
		[x: string]: any;
	}
}

const fixOdataHeaderFilter = (component: DevExpress.ui.dxDataGrid) => {
	const dataSource = component.instance().option('dataSource');
	// Check if it's an odata store
	if (
		dataSource != null &&
		typeof dataSource.store === 'function' &&
		dataSource.store()._requestDispatcher &&
		dataSource.store()._requestDispatcher.url &&
		dataSource.store()._requestDispatcher.url.toLowerCase().includes('odata')
	) {
		// Url from the store of the datagrid, use the helper method to fix the url for production builds
		const url = createUrlObject(dataSource.store()._requestDispatcher.url);

		// Query string parameters of the datagrid store
		const parameters: string[][] = Array.from<string[]>(<any>url.searchParams).filter(p => p[0] !== '$select');

		// Set the correct odata datasource on the header
		const columns = component.instance().option('columns');
		if (columns) {
			for (const column of columns) {
				// Skip if the column doesn't allow header filtering
				if (column.allowHeaderFiltering === false) continue;

				// Build the new url, only select and order by the field of the current column
				// Null check: Have to check this for production builds
				const newUrl =
					url.origin.toLowerCase() === 'null'
						? createUrlObject(url.pathname.replace(/\/(\/)*/g, '/'))
						: new URL(url.origin + url.pathname);
				parameters.forEach(p => newUrl.searchParams.set(p[0], p[1]));
				// newUrl.searchParams.set('$select', column.dataField);
				newUrl.searchParams.set('$apply', `groupby((${column.dataField}))`);
				newUrl.searchParams.set('$orderby', column.dataField);

				// Create the odata store
				const odataStore = new ODataStore({
					version: 4,
					url: getUrlObjectHref(newUrl),
					beforeSend: beforeSendEventArgs => dataSource.store()._requestDispatcher._beforeSend(beforeSendEventArgs),
					onLoading: options => {
						fixOdataDateOffsetFilter(options.filter);
					},
				});

				column.headerFilter = {
					dataSource: {
						pageSize: 100,
						// Not sure if we should pass the load options to the load method
						load: loadOptions => {
							// Call the odata store to load the data
							return odataStore.load(loadOptions).then(a =>
								a.map(record => {
									// Map the results to the expected format
									const text = record[column.dataField];
									const value = record[column.dataField];

									return {
										text,
										value,
									};
								})
							);
						},
					},
				};
			}
		}

		component.instance().option('columns', columns);
	}
};

/** default settings for DX grid */
export const dxDataGridDefaults: DevExpress.ui.dxDataGridOptions = {
	allowColumnReordering: true,
	columnResizingMode: 'nextColumn',
	rowAlternationEnabled: true,
	allowColumnResizing: true,
	columnHidingEnabled: false,
	showRowLines: true,
	showBorders: true,
	hoverStateEnabled: true,
	noDataText: '',
	columnFixing: {
		enabled: true,
	},
	columnChooser: {
		enabled: true,
		search: {
			enabled: true
		},
		title: 'Column chooser',
	},
	editing: {
		useIcons: true,
		texts: {
			confirmDeleteMessage: '',
		},
	},
	export: {
		allowExportSelectedData: false,
		enabled: true

	},
	onExporting: e => {
		const workbook = new Workbook();
		const worksheet = workbook.addWorksheet('Main sheet');
		exportDataGrid({
			component: e.component,
			worksheet: worksheet
		}).then(function() {
			workbook.xlsx.writeBuffer()
				.then(function(buffer: BlobPart) {
					saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
				});
		});
	},
	searchPanel: {
		visible: true,
	},
	paging: {
		pageSize: 25,
		enabled: true,
	},
	pager: {
		showPageSizeSelector: true,
		allowedPageSizes: [10, 25, 50, 100, 250],
		showInfo: true,
		visible: true,
		showNavigationButtons: true,
	},
	stateStoring: {
		savingTimeout: 500,
		type: 'localStorage',
	},
	onCellPrepared: e => {
		if (e.rowType === 'data' && e.column['command'] === 'edit') {
			const replaceIcon = (container: any, selector: string, tooltip: string, classesToRemove: string[], newClasses: string[]) => {
				const element: HTMLElement = container.querySelector(selector) as HTMLElement;

				if (element != null) {
					element.title = tooltip;
					//element.setAttribute('name', tooltip);
					element.textContent = '';
					//const icon = document.createElement('i');
					//element.appendChild(icon);
					classesToRemove.forEach(c => element.classList.remove(c));
					newClasses.forEach(c => element.classList.add(c));
				}
			};

			if (e.row.isEditing) {
				replaceIcon(e.cellElement, '.dx-link-save', 'Save', ['dx-icon-save'], ['far', 'fa-fw', 'fa-save', 'btn-save']);
				replaceIcon(e.cellElement, '.dx-link-cancel', 'Cancel', ['dx-icon-revert'], ['far', 'fa-fw', 'fa-undo', 'btn-undo']);
				replaceIcon(e.cellElement, '.dx-link-delete', 'Delete', ['dx-icon-trash'], ['far', 'fa-fw', 'fa-times', 'btn-delete']);
			} else {
				replaceIcon(e.cellElement, '.dx-link-edit', 'Edit', ['dx-icon-edit'], ['far', 'fa-fw', 'fa-pencil', 'btn-edit']);
				replaceIcon(e.cellElement, '.dx-link-delete', 'Delete', ['dx-icon-trash'], ['far', 'fa-fw', 'fa-times', 'btn-delete']);
			}
		}
	},
	onOptionChanged: (e: any) => {
		if (e.name === 'dataSource') {
			fixOdataHeaderFilter(e.component.instance());
		}
	},
	onInitialized: (e: any) => {
		fixOdataHeaderFilter(e.component);

		e.component.columnOption('command:edit', {
			fixed: true,
			fixedPosition: 'right',
		});
	},
	onEditorPreparing: (e: any) => {
		// Dont allow edit for splitted rows
		if (e.parentType !== 'dataRow') {
			return;
		}
		// Check if a lookup is set on the column
		if (e.lookup != null && e.format !== 'select') {
			// Replace the type of the editor, normally its a selectbox, but we want a lookup
			e.editorName = 'dxLookup';

			// Get the properties of the lookup setting property because its an object
			const props: Array<string> = Object.keys(e.lookup);

			// Skip the datasource
			for (let i = props.length - 1; i >= 0; i--) {
				if (['dataSource'].indexOf(props[i]) !== -1) {
					continue;
				}
				e.editorOptions[props[i]] = e.lookup[props[i]];
			}

			const width = window.innerWidth * 0.3;
			// Add extra options, this is not possible in the column
			e.editorOptions.popupWidth = () => width; // $(window).width() * 0.3;
			e.editorOptions.popupHeight = () => width; // $(window).width() * 0.3;
			e.editorOptions.usePopover = false;
			// e.editorOptions.onContentReady = this.addLookupToolbarButtons.bind(this);
		} else if (e.editorName === 'dxNumberBox') {
			e.editorOptions.step = 0;
			e.editorOptions.onKeyDown = edt => {
				// const nBox = edt.component.instance() as DevExpress.ui.dxNumberBox
				const event = edt.event as KeyboardEvent;
				// for now, just prevent doing anything on up/down
				// todo? add handling to focus nex/prev row. However dx doesn't seem to provide a straight forward method.
				if (event.key === 'ArrowDown') {
					event.stopPropagation();
					event.preventDefault();
					// nBox.blur()
					// e.component.focus()
				}
				if (event.key === 'ArrowUp') {
					event.stopPropagation();
					event.preventDefault();
					// nBox.blur()
					// e.component.focus()
				}
			};
		}
	},
};
